// angular
import {
  Component,
  computed,
  effect,
  inject,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
// components
import { ClimaticFiltersBoxComponent } from '@app/core/components/climatic-filters-box/climatic-filters-box.component';
import { FiltersBoxComponent } from '@app/core/components/filters-box/filters-box.component';
import { NestedFiltersBoxComponent } from '@app/core/components/nested-filters-box/nested-filters-box.component';
// services
import { CategoriesService } from '@app/+categories/shared/services/categories.service';
import { StorageService } from '@app/shared/services/storage.service';
import { CurrencyService } from '@app/core/shared/services/currency.service';
// types & enums
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
import { NestedFilter } from '@app/shared/types/nested-filter.type';
import {
  ClimaticFiltersType,
  PriceFilterType,
} from '@app/+products/shared/types/product-filters.type';
import { CategoriesType } from '@app/+categories/shared/types/categories.type';
import { I18n } from '@app/shared/enums/i18n.enum';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
// constants & env
import {
  climaticFilterItems,
  colorFilterItems,
  monthlyProductionFilterItems,
  stoneDesignFilterItems,
  stoneGradeFilterItems,
  surfaceFinishingFilterItems,
} from '@app/+products/shared/constants/product-filter-items';
import { environment } from '@environments/environment';

@Component({
  selector: 'pwj-products-filters',
  standalone: true,
  imports: [
    FiltersBoxComponent,
    TranslateModule,
    ClimaticFiltersBoxComponent,
    NestedFiltersBoxComponent,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './products-filters.component.html',
  styleUrl: './products-filters.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ProductsFiltersComponent {
  /** Wider overlay: no filter card chrome, expanded lists, footer actions live in filter boxes. */
  layoutVariant = input<'default' | 'dialog'>('default');
  filtersBoxPanelVariant = computed(() =>
    this.layoutVariant() === 'dialog' ? 'standalone' : 'embedded'
  );

  public categoriesTypes: CategoriesType[] = [];
  public categoriesTypesFilters: { _id: string; name: II18n }[] = [];
  public categories: CategoriesType[] = [];
  public categoriesFilters: { _id: string; name: II18n }[] = [];
  public subCategoriesFilters: { _id: string; name: II18n }[] = [];
  public selectedCategoryType!: string;
  public selectedCategory!: string;
  public currentLang: I18n = I18n[environment.defaultLang];

  public currentParams!: Params;

  // product filters
  public colorFilters: string[] = colorFilterItems;
  public surfaceFinishingFilters: string[] = surfaceFinishingFilterItems;
  public stoneDesignFilters: NestedFilter[] = stoneDesignFilterItems;
  public monthlyProductionFilters: string[] = monthlyProductionFilterItems;
  public stoneGradeFilters: string[] = stoneGradeFilterItems;
  public priceFilters: PriceFilterType = {
    min: 0,
    max: 20000000,
    step: 100000,
  };
  public climaticFilters: ClimaticFiltersType = climaticFilterItems;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;

  // dependencies
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);
  readonly categoriesService = inject(CategoriesService);
  private readonly _categoriesService = this.categoriesService;
  private readonly _currenciesService = inject(CurrencyService);
  private readonly _storageService = inject(StorageService);

  constructor() {
    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    effect(() => {
      this.currentCurrency = this._currenciesService.currentCurrency();
    });

    effect(() => {
      this.categoriesTypesFilters = this._categoriesService
        .categoriesData()
        ?.map(categoryType => {
          return {
            _id: categoryType._id,
            name: categoryType.title,
          };
        });
      this.categoriesTypes = this._categoriesService.categoriesData();
      this.getCategoriesDataByParams();
    });

    this._activatedRoute.queryParams.forEach(params => {
      // set params
      this.currentParams = params;

      this.getCategoriesDataByParams();
    });

  }

  public onSelectCategory(categoryType: string) {
    this.selectedCategoryType = categoryType;

    const getCategories = this.categoriesTypes?.find(
      item => item._id === categoryType
    );

    this.categoriesFilters =
      getCategories?.children?.map(category => ({
        _id: category._id,
        name: category.title,
      })) || [];
    this.categories = getCategories?.children || [];
  }

  public onSelectSubCategory(category: string) {
    this.selectedCategory = category;

    const getSubCategories = this.categories?.find(
      item => item._id === category
    );

    this.subCategoriesFilters =
      getSubCategories?.children?.map(category => ({
        _id: category._id,
        name: category.title,
      })) || [];
  }

  public applyFilter(
    filterValue: Params | string[] | null,
    filterQueryName?: string | null,
    isRangeInput?: boolean,
    isNestedFilters?: boolean
  ) {
    let queryParams: Params;

    if (isRangeInput && filterValue) {
      let priceFilter;

      if (this.currentCurrency === 'irr') {
        // set form value
        priceFilter = {
          lowest_price: JSON.stringify({
            irr: (filterValue as Params)?.['lowest_price'],
          }),
          highest_price: JSON.stringify({
            irr: (filterValue as Params)?.['highest_price'],
          }),
        };
      } else if (this.currentCurrency === 'usd') {
        // set form value
        priceFilter = {
          lowest_price: JSON.stringify({
            usd: (filterValue as Params)?.['lowest_price'],
          }),
          highest_price: JSON.stringify({
            usd: (filterValue as Params)?.['highest_price'],
          }),
        };
      }

      queryParams = {
        ...priceFilter,
        [filterQueryName as string]: null,
      };
    } else if (isNestedFilters && filterValue) {
      queryParams = filterValue;
    } else {
      queryParams = {
        take: null,
        skip: null,
        [filterQueryName as string]:
          filterValue?.length > 0 ? filterValue : null,
      };
    }

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  public getCategoriesDataByParams() {
    this.currentParams;

    if (this.currentParams['category_type_id']) {
      this.onSelectCategory(this.currentParams['category_type_id']);
    }

    if (this.currentParams['category_id']) {
      this.onSelectSubCategory(this.currentParams['category_id']);
    } else {
      this.subCategoriesFilters = [];
    }
  }
}
